import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import { isEmpty } from 'lodash';
import { useFlag } from 'flags';

import Button from 'components/Button/Button';
import { Card } from 'components/Card/Card';
import Loading from 'components/Loading/Loading';
import Tooltip from 'components/Tooltip/Tooltip';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import AudienceDropdown from './AudienceDropdown/AudienceDropdown';
import AdTemplateDropdown from './AdTemplateDropdown/AdTemplateDropdown';
import CreateAudienceModal from './CreateAudienceModal';
import AudSummary from './AudSummary';
import Icons from 'components/Icons';
import { defaultModal, openModal, showModal } from 'helpers/modals';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';
import TemplatePreview from './TemplatePreview/TemplatePreview';
import TemplateEditorCTA from './TemplateEditorCTA/TemplateEditorCTA';

import colors from 'theme/Colors.module.scss';
import './NewCampaign.scss';

import {
  Box,
  Text,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Radio,
  RadioGroup,
  useDisclosure,
} from '@chakra-ui/react';

import {
  useGetCampaignByIdQuery,
  useCreateCampaignMutation,
  useUpdateCampaignByIdMutation,
} from 'modules/campaigns/campaignsApi';

import { useGetAudienceByIdQuery } from 'modules/audiences/audienceApi';

const targetTypeAud = 'audience';
const targetTypeOpen = 'open';

const schema = yup.object().shape({
  name: yup.string().required('Campaign Name cannot be blank.').trim(),
  // audience: yup.number().required('Audience is required.'),
  maxPerFulfillment: yup
    .number()
    .required('Required.')
    .positive('Invalid')
    .integer(),
  priority: yup.number().positive('Invalid').integer(),
  useDateRange: yup.boolean(),
  startDate: yup.date().when('useDateRange', {
    is: true,
    then: yup.date().required('Start Date/Time is required'),
  }),
  endDate: yup.date().when('useDateRange', {
    is: true,
    then: yup
      .date()
      .min(yup.ref('startDate'), 'End Date should be greater than Start Date')
      .required('End Date/Time is required'),
  }),
  totalDeliveryLimit: yup.number().when('deliveryLimit', {
    is: false,
    then: yup
      .number()
      .required('No limit set, a limit is required.')
      .positive('Invalid delivery limit.')
      .integer(),
  }),
  customerFrequencyCap: yup.number().when('frequencyCapEnabled', {
    is: true,
    then: yup
      .number()
      .required(
        'No customer frequency cap set, must have a value of 1 or greater.'
      )
      .positive('Invalid customer frequency cap.')
      .integer(),
  }),
  adTemplate: yup
    .number()
    .required('Ad template is required.')
    .positive('Please select an ad template.')
    .integer(),
  holdoutPct: yup.number().when('holdoutEnabled', {
    is: true,
    then: yup
      .number()
      .required('Holdout percentage is required.')
      .min(1, 'Must be at least 1%.')
      .max(50, 'Cannot exceed 50%.'),
  }),
});

const NewCampaign = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: editID } = useParams();
  const location = useLocation();

  const isEditing = editID ? true : false;
  const [previewModal, setPreviewModal] = useState(defaultModal);

  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();
  const [audienceId, setAudienceId] = useState(0);
  const [initialForm, setInitialForm] = useState({
    name: '',
    fulfillCount: '',
    maxPerFulfillment: 1,
    audience: 0,
    priority: '',
    useDateRange: false,
    startDate: '',
    endDate: '',
    totalDeliveryLimit: '',
    deliveryLimit: true, // this name is misleading, it means 'no delivery limit' and matches that toggle switch value
    customerFrequencyCap: '',
    frequencyCapEnabled: false,
    adTemplate: 0,
    enabled: true,
    holdoutPct: 0,
    holdoutEnabled: false,
  });

  const title = isEditing ? 'Update your Campaign' : 'Create your New Campaign';
  const buttonIcon = isEditing ? null : 'add';
  const buttonText = isEditing ? 'Save Campaign' : 'Add Campaign';
  const isReadOnly = useFlag(['features', 'isReadOnly']);
  const [currentAudienceData, setCurrentAudienceData] = useState({});
  const [audienceDropdownRefreshToken, setAudienceDropdownRefreshToken] =
    useState(Date.now());

  const {
    data: campaignData,
    error: campaignDataError,
    isLoading: campaignDataIsLoading,
  } = useGetCampaignByIdQuery(editID, {
    skip: !editID, // Skip the query if companyId is null or undefined
  });

  const {
    data: audienceData,
    error: audienceDataError,
    isLoading: audienceDataIsLoading,
  } = useGetAudienceByIdQuery(audienceId, {
    skip: !audienceId, // Skip the query if audienceId is null or undefined
  });

  const [updateCampaignById] = useUpdateCampaignByIdMutation();
  const [createCampaign] = useCreateCampaignMutation();

  useEffect(() => {
    const prevData = campaignData;
    if (location.state && location.state.campaignValues) {
      setInitialForm(location.state.campaignValues);
    } else {
      autofill(prevData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignData, location.state]);

  useEffect(() => {
    if (location.state && location.state.campaignValues) {
      setAudienceId(location.state.campaignValues.audience);
    } else {
      setAudienceId(campaignData?.audience_id ?? 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignData?.audience_id, location.state]);

  const autofill = prevData => {
    if (!isEmpty(prevData)) {
      setInitialForm({
        name: prevData.name || '',
        fulfillCount: prevData.fulfill_count || '',
        maxPerFulfillment: prevData.max_per_fulfillment || 1,
        audience: +prevData.audience_id || 0,
        priority: prevData.priority || '',
        useDateRange: !!prevData.start_date || !!prevData.end_date || false,
        startDate: prevData.start_date
          ? new Date(prevData.start_date).toISOString().slice(0, 16)
          : '',
        endDate: prevData.end_date
          ? new Date(prevData.end_date).toISOString().slice(0, 16)
          : '',
        totalDeliveryLimit: prevData.delivery_limit || '',
        deliveryLimit: !prevData.delivery_limit || false,
        customerFrequencyCap: prevData.customer_delivery_limit || '',
        frequencyCapEnabled: prevData.customer_delivery_limit ? true : false,
        adTemplate: +prevData.ad_template_id || 0,
        enabled: !!prevData ? prevData.enabled : true,
        holdoutPct: prevData.holdout_pct || 0,
        holdoutEnabled: (prevData.holdout_pct || 0) > 0,
      });
    }
  };

  const handleSubmit = async (values, form) => {
    const {
      name,
      fulfillCount,
      maxPerFulfillment,
      audience,
      priority,
      deliveryLimit,
      totalDeliveryLimit,
      customerFrequencyCap,
      frequencyCapEnabled,
      useDateRange,
      startDate,
      endDate,
      adTemplate,
      enabled,
    } = values;

    const prepAudIDVal = () => {
      if (audience) {
        return audience > 0 ? +audience : null;
      } else {
        return null;
      }
    };

    const now = new Date();
    const localTimeOffset = now.getTimezoneOffset();
    const handleTimeOffset = time =>
      new Date(time.setMinutes(time.getMinutes() - localTimeOffset));

    const start_date = startDate ? handleTimeOffset(new Date(startDate)) : '';
    const end_date = endDate ? handleTimeOffset(new Date(endDate)) : '';

    let formData = {
      campaign: {
        name: name,
        fulfill_count: fulfillCount || null,
        max_per_fulfillment: maxPerFulfillment || 1,
        campaign_type: 'marketing',
        target_type: prepAudIDVal() ? targetTypeAud : targetTypeOpen,
        audience_id: prepAudIDVal(),
        priority: priority ? priority : null,
        delivery_limit: deliveryLimit
          ? null
          : (!!totalDeliveryLimit && totalDeliveryLimit) || null,
        customer_delivery_limit: frequencyCapEnabled
          ? customerFrequencyCap
          : null,
        start_date: useDateRange ? start_date : null,
        end_date: useDateRange ? end_date : null,
        ad_template_id: adTemplate > 0 ? +adTemplate : null,
        enabled: enabled,
        holdout_pct: values.holdoutEnabled ? (values.holdoutPct || 0) : 0,
      },
    };

    form.setSubmitting(true);

    if (isEditing) {
      try {
        await updateCampaignById({
          id: campaignData.id,
          data: formData,
        }).unwrap();

        dispatch(
          pushNotificationWithTimeout({
            type: 'success',
            message: 'Campaign updated successfully',
          })
        );
        redirectBack();
      } catch (error) {
        dispatch(
          pushNotificationWithTimeout({
            type: 'error',
            message: error.error,
          })
        );
      } finally {
        form.setSubmitting(false);
      }
    } else {
      try {
        formData.campaign.source_type = 'internal';
        await createCampaign(formData).unwrap();
        dispatch(
          pushNotificationWithTimeout({
            type: 'success',
            message: 'Campaign created successfully',
          })
        );
        redirectBack();
      } catch (error) {
        dispatch(
          pushNotificationWithTimeout({
            type: 'error',
            message: error.error,
          })
        );
      } finally {
        form.setSubmitting(false);
      }
    }
  };

  const redirectBack = () => {
    if (!location.pathname.includes('template-editor'))
      if (location.pathname.includes('campaigns')) {
        if (location?.state?.from === 'analytics') {
          navigate('/analytics');
        } else {
          navigate('/campaigns');
        }
      }
  };

  const showDateFields = (type, useDateRange) => {
    const fieldName = `${type.toLowerCase()}Date`;
    if (useDateRange) {
      return (
        <Box className="cmp-details__date-selection">
          <Flex>
            <Field name={fieldName}>
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors[fieldName] && form.touched[fieldName]}
                >
                  <FormLabel>{type} Date</FormLabel>
                  <Input
                    type="datetime-local"
                    value={field.value}
                    focusBorderColor={colors.primary600}
                    style={{ flex: 1 }}
                    onChange={e =>
                      form.setFieldValue(fieldName, e.target.value)
                    }
                  />
                  <FormErrorMessage>{form.errors[fieldName]}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          </Flex>
        </Box>
      );
    }
  };

  if (campaignDataIsLoading) {
    return <Loading type="fullpage" />;
  }

  if (campaignDataError) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch Campaign',
      })
    );
  }

  if (audienceDataError) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch Audience',
      })
    );
  }

  return (
    <>
      <Box className="new-cmp-header">
        <Box className="new-cmp-header__left">
          <Button
            type="button"
            url={
              location?.state?.from ? `/${location.state.from}` : '/campaigns'
            }
            icon="back"
            text="Back"
            textColor={colors.slate}
            btnStyle="transparent"
          />
        </Box>

        <Box className="new-cmp-header__nav">
          <Text as="p" fontWeight="600">
            {title}
          </Text>
        </Box>

        <Box className="new-cmp-header__right"></Box>
      </Box>
      <Formik
        enableReinitialize
        initialValues={initialForm}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {form => {
          const { isSubmitting, handleChange, values } = form;

          const includes =
            audienceData?.rule_sets?.filter(set => set.logical_op_type) || 0;
          const excludes =
            audienceData?.rule_sets?.filter(set => !set.logical_op_type) || 0;

          const valAsBool = useDateRange =>
            typeof useDateRange !== 'boolean'
              ? useDateRange === 'true'
              : useDateRange;

          const isValid = isEditing
            ? Object.keys(form.errors).length === 0
            : form.isValid;

          return (
            <Form className="new-cmp-form__container" name="new-cmp-form">
              <Box className="new-cmp-form__wrapper">
                <Card className="new-cmp-form__card">
                  <Flex gap={8} flexWrap="wrap">
                    <Flex flexDirection="column" flex="1 1 300px" minW="0">
                      <Field name="name">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={form.errors.name && form.touched.name}
                          >
                            <FormLabel fontWeight={400}>
                              Campaign Name
                            </FormLabel>
                            <Input
                              {...field}
                              placeholder="Name this Campaign"
                              focusBorderColor={colors.primary600}
                              disabled={isReadOnly || isSubmitting}
                            />
                            <FormErrorMessage>
                              {form.errors.name}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Flex>

                    <Flex flexDirection="column" flex="1 1 160px" minW="0">
                      <Field name="maxPerFulfillment">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.maxPerFulfillment &&
                              form.touched.maxPerFulfillment
                            }
                          >
                            <FormLabel fontWeight={400}>
                              Max/Fulfillment &nbsp;
                              <Tooltip
                                tipText="Maximum number of times this campaign will produce a fulfillment print for a single order.  Only applies to configurations that fulfill multiple prints with a single order."
                                direction="bottom"
                              />
                            </FormLabel>
                            <Input
                              {...field}
                              type="number"
                              placeholder="e.g 1"
                              focusBorderColor={colors.primary600}
                              disabled={isReadOnly || isSubmitting}
                            />
                            <FormErrorMessage>
                              {form.errors.maxPerFulfillment}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Flex>

                    <Flex flexDirection="column" flex="1 1 100px" minW="0">
                      <Field name="priority">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.priority && form.touched.priority
                            }
                          >
                            <FormLabel fontWeight={400}>
                              Priority &nbsp;
                              <Tooltip
                                tipText="Campaigns will be checked for audience match in the order of priority."
                                direction="bottom"
                              />
                            </FormLabel>
                            <Input
                              {...field}
                              type="number"
                              placeholder="e.g 1"
                              focusBorderColor={colors.primary600}
                              disabled={isReadOnly || isSubmitting}
                            />
                            <FormErrorMessage>
                              {form.errors.priority}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Flex>
                  </Flex>
                </Card>

                {/* Audience Details Section */}
                <FormLabel fontWeight={400}>Audience Details</FormLabel>
                <Card className="new-cmp-form__card">
                  <Flex flexDirection="column">
                    <Flex
                      className="new-cmp-form__aud-details"
                      flexDirection="column"
                    >
                      <CreateAudienceModal
                        isModalOpen={isModalOpen}
                        onModalClose={onModalClose}
                        audienceId={audienceId}
                        onUpdateAudience={aud => {
                          if (aud) {
                            setAudienceId(aud.id);
                            form.setFieldValue(
                              'audience',
                              aud?.id ? aud.id : 0
                            );
                            setAudienceDropdownRefreshToken(Date.now());
                          }
                        }}
                        onModalCancel={() => {
                          if (audienceId === 0) {
                            setAudienceId(currentAudienceData.id);
                            form.setFieldValue(
                              'audience',
                              currentAudienceData.id
                            );
                          }
                        }}
                      />
                      <Box className="new-aud-details__aud-dropdown">
                        <Field name="audience">
                          {({ form }) => (
                            <FormControl
                              isInvalid={
                                !form.errors.audience && !form.touched.audience
                              }
                            >
                              <FormLabel fontWeight={400}>Audience</FormLabel>
                              <AudienceDropdown
                                isDisabled={isReadOnly}
                                selectedValue={audienceId}
                                onChange={aud => {
                                  setAudienceId(aud?.id ?? 0);
                                  form.setFieldValue(
                                    'audience',
                                    aud?.id ? aud.id : 0
                                  );
                                }}
                                openNewAudience={() => {
                                  setCurrentAudienceData({
                                    id: audienceId,
                                  });
                                  setAudienceId(0);
                                  form.setFieldValue('audience', 0);
                                  onModalOpen();
                                }}
                                audienceDropdownRefreshToken={
                                  audienceDropdownRefreshToken
                                }
                              />
                              <FormErrorMessage>
                                {form.errors.audience}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </Box>
                      <Box className="new-aud-details__aud-rules">
                        <Flex paddingTop="1rem" justifyContent="space-between">
                          <FormLabel fontWeight={400}>Rules</FormLabel>
                          {audienceData &&
                            audienceData?.value !== 0 &&
                            !isReadOnly && (
                              <Flex
                                alignItems="center"
                                cursor="pointer"
                                gap="0.25rem"
                                marginBottom="8px"
                                onClick={() => {
                                  setAudienceId(audienceData?.id);
                                  onModalOpen();
                                }}
                              >
                                <Text as="p" fontSize="14px">
                                  Edit
                                </Text>
                                <Icons.Edit size={14} />
                              </Flex>
                            )}
                        </Flex>
                        {audienceDataIsLoading ? (
                          <Loading />
                        ) : (
                          <>
                            {audienceData &&
                            audienceId !== 0 &&
                            audienceData.rule_sets ? (
                              <Box
                                className="new-aud-details__aud-summary"
                                style={{
                                  backgroundColor: 'rgba(255,255,255,0.85)',
                                  padding: '1rem',
                                  border: '1px solid rgba(0,0,0,0.1)',
                                  fontSize: '15px',
                                }}
                              >
                                <AudSummary
                                  listBackgroundColor="rgba(0,0,0,0.0375)"
                                  data={{ includes, excludes }}
                                />
                              </Box>
                            ) : (
                              <Box
                                className="new-aud-details__aud-no-rules"
                                style={{
                                  background: 'rgba(0,0,0,0.02)',
                                  padding: '1rem',
                                  textAlign: 'center',
                                  fontSize: '15px',
                                }}
                              >
                                <i>NO RULES</i>
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    </Flex>
                  </Flex>
                </Card>

                {/* Campaign Details Section */}
                <FormLabel fontWeight={400}>Campaign Details</FormLabel>
                <Card className="new-cmp-form__card">
                  <Flex flexDirection="column">
                    <Flex
                      className="new-cmp-form__cmp-details"
                      flexDirection="column"
                    >
                      <Box className="cmp-details__status-section">
                        <Box className="cmp-details__status-options">
                          <FormLabel>Campaign Status</FormLabel>
                        </Box>

                        <Box className="cmp-details__status-attrs">
                          <FormLabel>Enabled:</FormLabel>
                          <ToggleSwitch
                            disabled={isSubmitting || isReadOnly}
                            name="enabled"
                            value={values.enabled}
                            onChange={handleChange}
                            color={colors.status00}
                          />
                        </Box>
                      </Box>

                      <Box className="cmp-details__delivery-section">
                        <Flex
                          className="cmp-details__delivery-options"
                          flexDirection="column"
                        >
                          <Field name="totalDeliveryLimit">
                            {({ field, form }) => (
                              <FormControl
                                isInvalid={
                                  form.errors.totalDeliveryLimit &&
                                  form.touched.totalDeliveryLimit
                                }
                              >
                                <FormLabel>
                                  Total Delivery Limit &nbsp;
                                  <Tooltip
                                    tipText="The number of deliveries to hit before this campaign turns off automatically."
                                    direction="top"
                                  />
                                </FormLabel>
                                <Input
                                  {...field}
                                  type="number"
                                  placeholder={
                                    values.deliveryLimit
                                      ? 'No Delivery Limit.'
                                      : 'Max Limit e.g. 125, 000'
                                  }
                                  focusBorderColor={colors.primary600}
                                  disabled={isReadOnly || values.deliveryLimit}
                                />
                                <FormErrorMessage>
                                  {form.errors.totalDeliveryLimit}
                                </FormErrorMessage>
                              </FormControl>
                            )}
                          </Field>
                        </Flex>

                        <Box className="cmp-details__delivery-attrs">
                          <FormLabel>No Limit:</FormLabel>
                          <ToggleSwitch
                            disabled={isSubmitting || isReadOnly}
                            name="deliveryLimit"
                            value={values.deliveryLimit}
                            onChange={handleChange}
                            color={colors.status00}
                          />
                        </Box>
                      </Box>

                      <Box className="cmp-details__frequency-section">
                        <Flex
                          className="cmp-details__frequency-options"
                          flexDirection="column"
                        >
                          <Field name="customerFrequencyCap">
                            {({ field, form }) => (
                              <FormControl
                                isInvalid={
                                  form.errors.customerFrequencyCap &&
                                  form.touched.customerFrequencyCap
                                }
                              >
                                <FormLabel>
                                  Customer Frequency Cap &nbsp;
                                  <Tooltip
                                    tipText="Enables a maximum number of times this campaign will be delivered to a customer."
                                    direction="top"
                                  />
                                </FormLabel>
                                <Input
                                  {...field}
                                  type="number"
                                  placeholder={
                                    values.frequencyCapEnabled ? '1' : 'None'
                                  }
                                  focusBorderColor={colors.primary600}
                                  disabled={
                                    isReadOnly || !values.frequencyCapEnabled
                                  }
                                />
                                <FormErrorMessage>
                                  {form.errors.customerFrequencyCap}
                                </FormErrorMessage>
                              </FormControl>
                            )}
                          </Field>
                        </Flex>

                        <Box className="cmp-details__frequency-attrs">
                          <FormLabel>Enabled:</FormLabel>
                          <ToggleSwitch
                            disabled={isSubmitting || isReadOnly}
                            name="frequencyCapEnabled"
                            value={values.frequencyCapEnabled}
                            onChange={handleChange}
                            color={colors.status00}
                          />
                        </Box>
                      </Box>

                      <Box className="cmp-details__holdout-section">
                        <Flex
                          className="cmp-details__holdout-options"
                          flexDirection="column"
                        >
                          <Field name="holdoutPct">
                            {({ field, form }) => (
                              <FormControl
                                isInvalid={
                                  form.errors.holdoutPct &&
                                  form.touched.holdoutPct
                                }
                              >
                                <FormLabel>
                                  Holdout % &nbsp;
                                  <Tooltip
                                    tipText="The percentage of customers who will NOT receive this campaign's insert. These customers form a control group used to measure the incremental lift (sales impact) of your campaign. For example, setting this to 10 means 10% of matched customers will be tracked but won't receive a printed insert."
                                    direction="top"
                                  />
                                </FormLabel>
                                <Input
                                  {...field}
                                  type="number"
                                  placeholder={
                                    values.holdoutEnabled
                                      ? 'e.g. 10'
                                      : 'Disabled'
                                  }
                                  focusBorderColor={colors.primary600}
                                  disabled={
                                    isReadOnly || !values.holdoutEnabled
                                  }
                                />
                                <FormErrorMessage>
                                  {form.errors.holdoutPct}
                                </FormErrorMessage>
                              </FormControl>
                            )}
                          </Field>
                        </Flex>

                        <Box className="cmp-details__holdout-attrs">
                          <FormLabel>Enabled:</FormLabel>
                          <ToggleSwitch
                            disabled={isSubmitting || isReadOnly}
                            name="holdoutEnabled"
                            value={values.holdoutEnabled}
                            onChange={handleChange}
                            color={colors.status00}
                          />
                        </Box>
                      </Box>

                      <Box className="cmp-details__schedule-section">
                        <Flex
                          className="cmp-details__schedule-options"
                          flexDirection="column"
                        >
                          <Field name="useDateRange">
                            {({ field, form }) => (
                              <FormControl
                                isInvalid={
                                  form.errors.useDateRange &&
                                  form.touched.useDateRange
                                }
                              >
                                <FormLabel>Schedule</FormLabel>
                                <RadioGroup
                                  defaultValue={false}
                                  onChange={() =>
                                    form.setFieldValue(
                                      field.name,
                                      !valAsBool(values.useDateRange)
                                    )
                                  }
                                  value={values.useDateRange}
                                >
                                  <Flex
                                    flexDirection="column"
                                    gap="1rem"
                                    marginTop="1rem"
                                    marginBottom="1rem"
                                  >
                                    <Radio
                                      display="flex"
                                      flexDirection="row"
                                      value={false}
                                      colorScheme="orange"
                                    >
                                      Run continuously starting today
                                    </Radio>
                                    <Radio
                                      display="flex"
                                      flexDirection="row"
                                      value={true}
                                      colorScheme="orange"
                                    >
                                      Set start and end dates
                                    </Radio>
                                  </Flex>
                                </RadioGroup>
                              </FormControl>
                            )}
                          </Field>
                        </Flex>

                        <Box className="cmp-details__schedule-dates">
                          <Flex gap={2} flexWrap="wrap">
                            <Flex
                              flexDirection="column"
                              flex="1 1 200px"
                              minW="0"
                            >
                              {showDateFields('Start', values.useDateRange)}
                            </Flex>
                            <Flex
                              flexDirection="column"
                              flex="1 1 200px"
                              minW="0"
                            >
                              {showDateFields('End', values.useDateRange)}
                            </Flex>
                          </Flex>
                        </Box>
                      </Box>

                      <Box className="cmp-details__ad-template-section">
                        <Flex flexDirection="column">
                          <Field name="adTemplate">
                            {({ field, form }) => (
                              <FormControl
                                isInvalid={
                                  form.errors.adTemplate &&
                                  form.touched.adTemplate
                                }
                              >
                                <FormLabel>Ad Template</FormLabel>
                                <AdTemplateDropdown
                                  initialForm={initialForm}
                                  onChange={e => {
                                    form.setFieldValue(
                                      field.name,
                                      e && e.value !== '' ? e.value : 0
                                    );
                                  }}
                                  isDisabled={isReadOnly}
                                />
                                <FormErrorMessage>
                                  {form.errors.adTemplate}
                                </FormErrorMessage>
                              </FormControl>
                            )}
                          </Field>
                        </Flex>

                        {!isReadOnly && (
                          <TemplateEditorCTA
                            templateID={values.adTemplate}
                            campaignValues={values}
                            editID={editID}
                          />
                        )}

                        {!isReadOnly && (
                          <TemplatePreview
                            templateID={values.adTemplate}
                            openModal={openModal}
                            setPreviewModal={setPreviewModal}
                          />
                        )}
                      </Box>
                    </Flex>
                  </Flex>
                </Card>
              </Box>
              {!isReadOnly && (
                <Flex flexDirection="column" align="center">
                  <Button
                    type="submit"
                    icon={buttonIcon}
                    iconColor={colors.white}
                    text={buttonText}
                    textColor={colors.white}
                    textHoverColor={colors.white}
                    hoverColor={colors.primary500}
                    btnStyle="solid"
                    disabled={isSubmitting}
                    callback={() => {
                      if (!isValid) {
                        dispatch(
                          pushNotificationWithTimeout({
                            type: 'error',
                            message:
                              'Form errors: please see fields for details.',
                          })
                        );
                      }
                    }}
                  />
                </Flex>
              )}
            </Form>
          );
        }}
      </Formik>

      {showModal(previewModal, setPreviewModal)}
    </>
  );
};

export default NewCampaign;
